/**
 * Correlation trace IDs — cheap in-browser equivalent of an
 * AsyncLocalStorage. Lets one user gesture (e.g. "Generate drills")
 * get a single ID that follows every subsystem it touches.
 *
 * Browser has no AsyncLocalStorage, so the "active" trace is a
 * module-level ref that callers set with `withTrace(id, fn)` at the
 * gesture boundary. Async hops inside `fn` inherit it transparently
 * as long as they don't cross a setTimeout / event callback where
 * the microtask queue detaches — for those hops callers pass the
 * id explicitly (or wrap the boundary with `withTrace` again).
 *
 * Trace IDs are opaque strings (11 chars). Never sent to Anthropic,
 * never persisted — pure in-browser correlation tag.
 */

const TRACE_PREFIX = 't';
let activeTrace: string | null = null;

/** Generate a new trace id. No crypto dep — 11 chars of base36 noise
 *  is plenty to visually disambiguate in a log panel. */
export function newTraceId(): string {
  const a = Math.random().toString(36).slice(2, 8);
  const b = Date.now().toString(36).slice(-4);
  return `${TRACE_PREFIX}_${a}${b}`;
}

/** Run `fn` with `id` as the active trace. Restores the previous
 *  active trace afterwards, even if `fn` throws. */
export async function withTrace<T>(id: string, fn: () => Promise<T>): Promise<T> {
  const prev = activeTrace;
  activeTrace = id;
  try { return await fn(); }
  finally { activeTrace = prev; }
}

/** Synchronous variant. */
export function withTraceSync<T>(id: string, fn: () => T): T {
  const prev = activeTrace;
  activeTrace = id;
  try { return fn(); }
  finally { activeTrace = prev; }
}

/** Start a new trace and make it active. Pair with `endTrace()` when
 *  you have a clear gesture boundary but can't use `withTrace`. */
export function startTrace(label?: string): string {
  const id = newTraceId();
  activeTrace = id;
  // Label is purely advisory — debug.ts picks it up via the next
  // log entry's trace field plus any `labelTrace` call the caller
  // makes.
  if (label) labels.set(id, label);
  return id;
}

export function endTrace(id: string): void {
  if (activeTrace === id) activeTrace = null;
  labels.delete(id);
}

/** Attach a human label to a trace id so the DebugPanel can show
 *  "t_ab12cd34 · generate-drills". Purely cosmetic. */
const labels = new Map<string, string>();
export function labelTrace(id: string, label: string): void {
  labels.set(id, label);
}
export function getTraceLabel(id: string): string | undefined {
  return labels.get(id);
}

/** Currently active trace id, or null. */
export function getActiveTrace(): string | null {
  return activeTrace;
}
